import { useCallback, useEffect, useRef } from "react";
import { useData } from "@hooks/useData";
import Http from "@core/fetch";
import { ID } from "@datassential/platform-ui-lib";
import {
    ApiModel,
    DEFAULT_FOOD_SOCIAL_CUSTOM_ASSOCIATION,
    IFoodSocialCustomAssociation,
    mapFoodSocialCustomAssociation,
} from "@models/FoodSocialCustomAssociation";

export function useFoodSocialCustomAssociation(wordId: ID, query: string) {
    const controllerRef = useRef<AbortController | null>(null);

    const idleFetchFn = useCallback(async (): Promise<IFoodSocialCustomAssociation> => {
        controllerRef.current?.abort();
        return DEFAULT_FOOD_SOCIAL_CUSTOM_ASSOCIATION;
    }, []);

    const getCustomAssociation = useCallback(async (id: ID, q: string): Promise<IFoodSocialCustomAssociation> => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;

        const response: ApiModel = await Http.get(
            'get food profile custom association',
            { keywordId: id, query: q },
            { signal: controller.signal },
        );

        if (typeof response?.matchCount !== 'number') {
            throw new Error('get food profile custom association: unexpected response');
        }

        return {
            ...mapFoodSocialCustomAssociation(response),
            query: response.query ?? q,
        };
    }, []);

    useEffect(() => () => controllerRef.current?.abort(), []);

    const fetchFn = query.trim() === '' ? idleFetchFn : getCustomAssociation;

    return useData(DEFAULT_FOOD_SOCIAL_CUSTOM_ASSOCIATION, fetchFn, wordId, query);
}
